import type { RegisterInput, RequestLoginCodeInput, VerifyLoginCodeInput, UpdateProfileInput, User } from '../types';

import { apiClient } from './api-client';
import { tokenManager } from './token-manager';

export class AuthAPI {
  async register(data: RegisterInput): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/auth/register', data, { requiresAuth: false });
  }

  async requestLoginCode(data: RequestLoginCodeInput): Promise<{ message: string; code?: string }> {
    return apiClient.post<{ message: string; code?: string }>('/auth/request-code', data, { requiresAuth: false });
  }

  async verifyLoginCode(data: VerifyLoginCodeInput): Promise<{ accessToken: string; user: User }> {
    const { accessToken, user } = await apiClient.post<{ accessToken: string; user: User }>('/auth/verify-code', data, {
      requiresAuth: false,
    });

    tokenManager.setToken(accessToken);
    return { accessToken, user };
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout', undefined, { requiresAuth: false });
    } finally {
      tokenManager.clearToken();
    }
  }

  async getCurrentUser(): Promise<User | null> {
    // Skip the request if no token is available
    if (!tokenManager.getToken()) {
      return null;
    }

    try {
      const response = await apiClient.get<User>('/auth/me');
      return response;
    } catch (error: unknown) {
      // Return null for 401 errors
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        return null;
      }
      throw error;
    }
  }

  async updateUserProfile(data: UpdateProfileInput): Promise<User> {
    const response = await apiClient.put<User>('/auth/profile', data);
    return response;
  }
}

export const authAPI = new AuthAPI();
